/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import auth from "@/auth/authService";

Vue.use(Router)

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior () {
        return { x: 0, y: 0 }
    },
    routes: [

        {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
            path: '',
            component: () => import('./layouts/main/Main.vue'),
            children: [
        // =============================================================================
        // Theme Routes
        // =============================================================================
                {
                    path: '/',
                    redirect: '/dashboard/analytics'
                },
                {
                    path: '/dashboard/analytics',
                    name: 'dashboardAnalytics',
                    component: () => import('./views/DashboardAnalytics.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/dashboard/statistics',
                    name: 'dashboardStatistics',
                    component: () => import('./views/DashboardStatistics.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
        // =============================================================================
        // MATCH
        // =============================================================================
                {
                    path: '/match/list',
                    name: 'matchList',
                    component: () => import('./views/match/List.vue'),
                    meta: {
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: '我的赛事', url: '/match/list' },
                            { title: '参赛记录', active: true }
                        ],
                        pageTitle: '参赛记录',
                        rule: 'user'
                    },
                },
                {
                    path: '/match/detail',
                    name: 'matchDetail',
                    component: () => import('./views/match/Detail.vue'),
                    meta: {
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: '我的赛事', url: '/match/list' },
                            { title: '赛事详情', active: true }
                        ],
                        pageTitle: '赛事详情',
                        rule: 'user'
                    }
                },
        // =============================================================================
        // TEAMS
        // =============================================================================
                {
                    path: '/team/create-list',
                    name: 'teamList',
                    component: () => import('./views/team/CreateList.vue'),
                    meta: {
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: '我的团队', url: null },
                            { title: '创建的团队', active: true }
                        ],
                        pageTitle: '创建的团队',
                        rule: 'user'
                    },
                },
                {
                    path: '/team/join-list',
                    name: 'teamList',
                    component: () => import('./views/team/JoinList.vue'),
                    meta: {
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: '我的团队', url: null },
                            { title: '加入的团队', active: true }
                        ],
                        pageTitle: '加入的团队',
                        rule: 'user'
                    },
                },
                {
                    path: '/team/detail',
                    name: 'teamList',
                    component: () => import('./views/team/Detail.vue'),
                    meta: {
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: '创建的团队', url: '/team/create-list' },
                            { title: '团队信息', active: true }
                        ],
                        pageTitle: '团队信息',
                        rule: 'user'
                    },
                },
        // =============================================================================
        // USERS
        // =============================================================================
                {
                    path: '/user/profile',
                    name: 'userProfile',
                    component: () => import('./views/user/Profile.vue'),
                    meta: {
                        breadcrumb: [
                            { title: 'Home', url: '/' },
                            { title: '个人信息', active: true }
                        ],
                        pageTitle: '个人信息',
                        rule: 'user'
                    },
                },
        // =============================================================================
        // Message
        // =============================================================================
                {
                    path: '/message/list',
                    name: 'message/List',
                    component: () => import('./views/message/List.vue'),
                    meta: {
                        breadcrumb: [
                            { title: 'Home', 'url': '/' },
                            { title: '消息列表', active: true }, 
                        ],
                        pageTitle: '消息列表',
                        rule: 'user'
                    }
                }
            ],
        },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
        {
            path: '',
            component: () => import('@/layouts/full-page/FullPage.vue'),
            children: [
        // =============================================================================
        // PAGES
        // =============================================================================
                {
                    path: '/pages/login',
                    name: 'pageLogin',
                    component: () => import('@/views/pages/Login.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/register',
                    name: 'pageRegister',
                    component: () => import('@/views/pages/Register.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/forgot-password',
                    name: 'pageForgotPassword',
                    component: () => import('@/views/pages/ForgotPassword.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/reset-password',
                    name: 'pageResetPassword',
                    component: () => import('@/views/pages/ResetPassword.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/lock-screen',
                    name: 'pageLockScreen',
                    component: () => import('@/views/pages/LockScreen.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/comingsoon',
                    name: 'pageComingSoon',
                    component: () => import('@/views/pages/ComingSoon.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/error-404',
                    name: 'pageError404',
                    component: () => import('@/views/pages/Error404.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/error-500',
                    name: 'pageError500',
                    component: () => import('@/views/pages/Error500.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/not-authorized',
                    name: 'pageNotAuthorized',
                    component: () => import('@/views/pages/NotAuthorized.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
                {
                    path: '/pages/maintenance',
                    name: 'pageMaintenance',
                    component: () => import('@/views/pages/Maintenance.vue'),
                    meta: {
                        rule: 'user'
                    }
                },
            ]
        },
        // Redirect to 404 page, if no match found
        {
            path: '*',
            redirect: '/pages/error-404'
        }
    ],
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = "none";
    }
})

router.beforeEach((to, from, next) => {

    if (
        to.path === "/pages/login" ||
        to.path === "/pages/forgot-password" ||
        to.path === "/pages/error-404" ||
        to.path === "/pages/error-500" ||
        to.path === "/pages/register" ||
        to.path === "/pages/comingsoon" ||
        (auth.isAuthenticated())
    ) {
        return next();
    }
    console.log(to.fullPath)
    // 登录后带参数跳转
    router.push({ path: '/pages/login', query: { to: to.fullPath } })
    // Specify the current path as the customState parameter, meaning it
    // will be returned to the application after auth
    // auth.login({ target: to.path });

});

export default router
